import { DoneButton } from 'components/blue-button'
import DismissButton from 'components/dismiss-button'
import { Selector } from 'components/selector'
import { useState } from 'react'
import { useRenameClassMutation, useUpdateClassDetailsMutation } from 'util/rtk-api'
import { SUBJECT_OPTIONS } from 'util/subject-options'
import styles from 'css/components/classrooms/create-class-overlay.module.css'

// Matches the grade options offered at class creation (create-class-overlay.tsx)
// -- an edit of a class must offer the same choices it was created with, not
// a smaller/different set (the mockup's own K-12 list has no Transition(18+)
// option, which is real and already in production; parity governs visuals,
// never a data shape we've already decided).
const GRADE_OPTIONS: [value: string, label: string][] = [
    ['k', 'Kindergarten'],
    ...Array.from({ length: 12 }, (_, i) => [String(i + 1), `Grade ${i + 1}`] as [string, string]),
    ['t', 'Transition(18+)'],
]
const PERIOD_OPTIONS = ['All Day', '1', '2', '3', '4', '5', '6', '7', '8']

interface RenameClassOverlayProps {
    classId: string
    currentName: string
    currentGrades?: string[]
    currentPeriod?: string
    currentSubject?: string
    setShowRenameClassOverlay: (show: boolean) => void
}

export const RenameClassOverlay = ({ classId, currentName, currentGrades, currentPeriod, currentSubject, setShowRenameClassOverlay }: RenameClassOverlayProps) => {
    const [newName, setNewName] = useState(currentName)
    const [grades, setGrades] = useState<string[]>(currentGrades || [])
    const [period, setPeriod] = useState(currentPeriod || '')
    const [subject, setSubject] = useState(currentSubject || '')
    const [gradesTouched, setGradesTouched] = useState(false)
    const [renameClass, { isLoading: isRenaming }] = useRenameClassMutation()
    const [updateClassDetails, { isLoading: isUpdatingDetails }] = useUpdateClassDetailsMutation()
    const isLoading = isRenaming || isUpdatingDetails

    const gradesValid = grades.length > 0
    const canSave = newName.trim() && gradesValid && period && subject

    const toggleGrade = (value: string) => {
        setGradesTouched(true)
        setGrades(prev => prev.includes(value) ? prev.filter(g => g !== value) : [...prev, value])
    }

    const handleSave = async () => {
        setGradesTouched(true)
        if (!canSave) return

        try {
            if (newName.trim() !== currentName) {
                await renameClass({ classId, name: newName.trim() })
            }
            const detailsChanged = JSON.stringify([...grades].sort()) !== JSON.stringify([...(currentGrades || [])].sort())
                || period !== currentPeriod || subject !== currentSubject
            if (detailsChanged) {
                await updateClassDetails({ classId, grades, period, subject })
            }
            setShowRenameClassOverlay(false)
        } catch (error) {
            console.error('Error updating class details:', error)
        }
    }

    return (
        <div className="overlay">
            <div className="overlay-content-container">
                <div className="overlay-content-header">
                    <h3>Edit class details</h3>
                    <DismissButton onClick={() => setShowRenameClassOverlay(false)} />
                </div>
                <div className={`overlay-content-body ${styles.overlayContentBody}`}>
                    <div className={styles.flexColumn}>
                        <label>Class Name:</label>
                        <input
                            type="text"
                            value={newName}
                            onChange={(e) => setNewName(e.target.value)}
                            placeholder="Enter class name"
                            autoFocus
                        />
                    </div>
                    <div className={styles.flexColumn}>
                        <label htmlFor="grade">Grades:
                            <div className={styles.gray}>(Select all that apply)</div>
                        </label>
                        <div className={styles.checkboxGroup}>
                            {GRADE_OPTIONS.map(([value, label]) => (
                                <label key={value}>
                                    <input
                                        type="checkbox"
                                        name="grade"
                                        value={value}
                                        checked={grades.includes(value)}
                                        onChange={() => toggleGrade(value)}
                                    />
                                    {label}
                                </label>
                            ))}
                        </div>
                        {gradesTouched && !gradesValid && (
                            <div className={styles.errorText}>Choose at least one grade.</div>
                        )}
                    </div>
                    <div className={styles.classroomFormRow}>
                        <Selector
                            options={PERIOD_OPTIONS}
                            onSelect={(value) => setPeriod(value || '')}
                            style={styles.width325}
                            placeholder="Period"
                            required
                            value={period}
                        />
                        <Selector
                            options={SUBJECT_OPTIONS}
                            onSelect={(value) => setSubject(value || '')}
                            style={styles.width325}
                            placeholder="Subject"
                            required
                            value={subject}
                        />
                    </div>
                    <div className={styles.alignRight}>
                        <DoneButton onClick={handleSave} disabled={isLoading || !canSave} />
                    </div>
                </div>
            </div>
        </div>
    )
}
